import { useEffect, useState } from 'react';
import { avisar, avisarErro } from './Avisos.tsx';
import { Icone } from './Icone.tsx';
import { JanelaEmBlocos } from './PassoAPasso.tsx';
import { usarDialogo } from '../lib/dialogo.ts';
import {
  apiComercial,
  type ModeloDeAbordagem,
  type ParagrafoDoModelo,
  type TrechoDoModelo,
} from '../lib/api.ts';

/**
 * ⭐⭐ OS TEXTOS DE ABORDAGEM, dentro da aba — 20/09/2026 (PRD §49.10).
 *
 * > *"Em alguma parte da aba Comercial precisa ter os modelos dos e-mails e
 * > WhatsApp, pois assim fica mais fácil para copiar o modelo."* — Richard
 *
 * ⭐⭐ **São TRÊS, e eram oito — decisão dele em 21/09/2026.** *"Acredito que o
 * melhor enviar o mesmo e-mail para todos as empresas que irei contatar."* A
 * janela abre no e-mail, que é o primeiro toque de toda empresa.
 *
 * ⭐ **O nome da empresa já vem dentro do texto**, e não sobra marcador nenhum
 * para preencher à mão.
 *
 * ⛔⛔ **O Hub NÃO MANDA NADA.** Ele monta o texto, a pessoa copia e envia da
 * caixa da casa. Disparo pelo Hub queimaria a reputação do domínio — e aí cai
 * em spam também o que vai para cliente.
 *
 * ⚠️ **O que o Hub não sabe, ele não inventa.** Marcador que sobre aparece no
 * alto, em vermelho: texto que PARECE pronto e não está é pior que texto
 * visivelmente incompleto.
 *
 * ⭐⭐ **O TEXTO TEM NEGRITO E COR desde 21/09/2026**, e por isso o botão copia
 * **duas versões de uma vez**: a formatada, que o Gmail cola com o negrito e a
 * cor, e a simples, para onde formatação não entra. Quem escolhe é o programa
 * onde se cola. ⛔ **Copiar só a simples traria `**INSIGHT**` com os asteriscos
 * à mostra; copiar só a formatada estragaria a colagem no WhatsApp.**
 */
/*
 * ⭐ **A aba mostra o CANAL, e não a letra — 21/09/2026.** Com seis e-mails, a
 * letra era o nome do texto e a pessoa a conhecia da planilha. Com um e-mail
 * só, "A" não diz nada: o que se procura na janela é *o e-mail* ou *o
 * WhatsApp*.
 */
function rotuloDaAba(m: ModeloDeAbordagem, todos: ModeloDeAbordagem[]): string {
  /* ⭐ 13/10/2026: com a retomada por e-mail (E2) são dois e-mails, e duas abas
     "E-mail" lado a lado não diziam qual era qual. Cada canal numera os seus. */
  const nome = m.canal === 'email' ? 'E-mail' : 'WhatsApp';
  const doCanal = todos.filter((x) => x.canal === m.canal);
  return doCanal.length > 1 ? `${nome} ${doCanal.indexOf(m) + 1}` : nome;
}

/**
 * A cor do nível, das variáveis da casa.
 *
 * ⛔ **Nenhum hexadecimal aqui.** As três cores moram em `tokens.css`, e é de lá
 * que elas vêm — *cor não se inventa* (CLAUDE.md). ⚠️ Para a **cópia**, porém, o
 * e-mail precisa do valor resolvido: `var(--nivel-insight)` não significa nada
 * dentro do Gmail. Por isso a cópia lê o valor calculado do próprio elemento.
 */
const VARIAVEL_DO_NIVEL = {
  insight: '--nivel-insight',
  forensic: '--nivel-forensic',
  kairos: '--nivel-kairos',
} as const;

const estiloDoTrecho = (tr: TrechoDoModelo) =>
  tr.nivel ? { color: `var(${VARIAVEL_DO_NIVEL[tr.nivel]})` } : undefined;

/** As três cores, já resolvidas em hexadecimal, para a cópia. */
function coresDosNiveis(): Record<string, string> {
  const folha = getComputedStyle(document.documentElement);
  const saida: Record<string, string> = {};
  for (const [nivel, variavel] of Object.entries(VARIAVEL_DO_NIVEL)) {
    saida[nivel] = folha.getPropertyValue(variavel).trim();
  }
  return saida;
}

/**
 * O texto formatado, em HTML, para a área de transferência.
 *
 * ⚠️ **O estilo vai EM CADA TRECHO (`style=`), e não numa folha.** O Gmail
 * descarta folha de estilo ao colar; o que ele preserva é o atributo na tag.
 *
 * ⚠️ **As cores chegam resolvidas**, lidas do próprio documento: dentro do
 * e-mail não existe `var(--nivel-insight)`.
 */
function emHtml(paragrafos: ParagrafoDoModelo[], cores: Record<string, string>): string {
  const esc = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const doTrecho = (tr: TrechoDoModelo) => {
    let dentro = esc(tr.t);
    if (tr.i) dentro = `<em>${dentro}</em>`;
    if (tr.n) dentro = `<strong>${dentro}</strong>`;
    const cor = tr.nivel ? cores[tr.nivel] : undefined;
    return cor ? `<span style="color:${cor}">${dentro}</span>` : dentro;
  };
  /*
   * ⚠️ **Parágrafo vazio vira uma linha em branco, não um `<p>` vazio.** O
   * Gmail come `<p></p>` e o e-mail chegaria com os parágrafos grudados.
   */
  /*
   * ⭐⭐ **Tópicos viram `<ul><li>` de verdade — 22/09/2026.** Ele reformatou os
   * três níveis como lista no Gmail; sem isto, refaria os marcadores à mão a
   * cada envio, como fez com o vocativo em 21/09.
   *
   * ⚠️ **Os itens VIZINHOS entram na MESMA lista.** Um `<ul>` por item deixa o
   * Gmail abrir três listas seguidas, com o espaço de parágrafo entre elas — e
   * o bloco volta a pesar, que era o que ele quis resolver.
   */
  const saida: string[] = [];
  let naLista = false;
  for (const par of paragrafos) {
    const linha = par.trechos.map(doTrecho).join('');
    if (par.lista) {
      if (!naLista) {
        saida.push('<ul style="margin:0 0 1em 0;padding-left:1.5em">');
        naLista = true;
      }
      saida.push(`<li style="margin-bottom:0.5em">${linha}</li>`);
      continue;
    }
    if (naLista) {
      saida.push('</ul>');
      naLista = false;
    }
    saida.push(linha.trim() ? `<div>${linha}</div>` : '<div><br></div>');
  }
  if (naLista) saida.push('</ul>');
  return saida.join('');
}

export function ModelosDeAbordagem({
  prospeccaoId,
  aoFechar,
}: {
  prospeccaoId: string;
  aoFechar: () => void;
}) {
  const [modelos, setModelos] = useState<ModeloDeAbordagem[] | null>(null);
  const [saudacao, setSaudacao] = useState('');
  /* ⭐ 16/10/2026: o e-mail de quem decide, como na janela do e-mail de entrega. */
  const [para, setPara] = useState('');
  const [aberto, setAberto] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  usarDialogo(aoFechar);

  useEffect(() => {
    apiComercial
      .modelos(prospeccaoId)
      .then((r) => {
        setModelos(r.modelos);
        setSaudacao(r.saudacao ?? '');
        setPara(r.para ?? '');
        setAberto(r.indicado || r.modelos[0]?.id || '');
      })
      .catch(() => setErro('Não consegui carregar os textos.'));
  }, [prospeccaoId]);

  const m = modelos?.find((x) => x.id === aberto) ?? null;

  const copiar = (texto: string, oQue: string) => {
    void navigator.clipboard
      ?.writeText(texto)
      .then(() => avisar(`${oQue} copiado.`))
      .catch(() => avisarErro('O navegador não deixou copiar. Selecione o texto e copie à mão.'));
  };

  /**
   * Copia o texto **com** o negrito e a cor, e **também** sem eles.
   *
   * ⚠️ **Os dois formatos vão no mesmo item da área de transferência**, e quem
   * escolhe é o programa onde se cola: o Gmail pega o formatado, o bloco de
   * notas e o WhatsApp pegam o simples.
   *
   * ⛔ **`ClipboardItem` não existe em todo navegador**, e onde faltar a cópia
   * cai no texto simples com um aviso — melhor um e-mail sem negrito que um
   * botão que não faz nada.
   */
  const copiarFormatado = (m: ModeloDeAbordagem) => {
    const cores = coresDosNiveis();
    const html = emHtml(m.paragrafos, cores);
    const item =
      typeof ClipboardItem === 'undefined'
        ? null
        : new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([m.corpo], { type: 'text/plain' }),
          });
    if (!item || !navigator.clipboard?.write) {
      copiar(m.corpo, 'Texto');
      return;
    }
    void navigator.clipboard
      .write([item])
      .then(() => avisar('Texto copiado, com o negrito e as cores.'))
      .catch(() => copiar(m.corpo, 'Texto'));
  };

  return (
    <JanelaEmBlocos
      rotulo="Textos de abordagem"
      sobrelinha="Comercial · como escrever"
      titulo="Os textos de abordagem"
      sub="Já com o nome da empresa e de quem decide dentro. O Hub monta; quem envia é você, da caixa da casa."
      largura="media"
      aoFechar={aoFechar}
      erro={erro}
      rodape={
        <>
          <button type="button" className="imv-bt fantasma" onClick={aoFechar}>Fechar</button>
          <span className="imv-esp" />
          {m && m.canal === 'email' && para && (
            <button type="button" className="imv-bt fantasma" onClick={() => copiar(para, 'E-mail')}>
              <Icone nome="copiar" tamanho={13} /> Copiar o e-mail
            </button>
          )}
          {m && m.assunto && (
            <button type="button" className="imv-bt fantasma" onClick={() => copiar(m.assunto, 'Assunto')}>
              <Icone nome="copiar" tamanho={13} /> Copiar o assunto
            </button>
          )}
          {m && (
            <button type="button" className="imv-bt primario" onClick={() => copiarFormatado(m)}>
              <Icone nome="copiar" tamanho={14} /> Copiar o texto
            </button>
          )}
        </>
      }
    >
      {!modelos ? (
        <p className="fp-vazio">Carregando os textos…</p>
      ) : (
        <>
          <div className="mo-abas" role="tablist" aria-label="Modelos de texto">
            {modelos.map((x) => (
              <button
                key={x.id}
                type="button"
                role="tab"
                aria-selected={x.id === aberto}
                className={`mo-aba${x.id === aberto ? ' ativa' : ''}`}
                onClick={() => setAberto(x.id)}
                title={x.paraQuem}
              >
                {rotuloDaAba(x, modelos)}
              </button>
            ))}
          </div>

          {m && (
            <div className="mo-corpo">
              <p className="mo-paraquem">
                {m.canal === 'email' ? 'E-mail' : 'WhatsApp'} · {m.paraQuem}
              </p>

              {/* ⚠️ Antes do texto, e não depois: é o que a pessoa precisa saber
                  ANTES de copiar, e um aviso no pé se lê tarde demais. */}
              {m.aPreencher.length > 0 && (
                <p className="mo-faltam">
                  <Icone nome="atencao" tamanho={14} />
                  <span>
                    Falta completar antes de enviar:{' '}
                    {m.aPreencher.map((x, i) => (
                      <span key={x}>
                        {i > 0 && ', '}
                        <code>{x}</code>
                      </span>
                    ))}
                  </span>
                </p>
              )}

              {/*
                ⭐⭐ **O texto foi montado para AGORA, e a tela diz isso.**
                A saudação sai da hora do servidor; copiar às 17h e enviar às
                19h manda uma "boa tarde" fora de hora, e o Hub não tem como
                adivinhar quando a mensagem vai sair. ⚠️ Aviso é o que se pode
                fazer: bloquear seria pior, porque o envio é dele.
              */}
              {saudacao && (
                <p className="mo-hora">
                  <Icone nome="relogio" tamanho={13} />
                  <span>
                    Montado com <strong>{saudacao}</strong>, pela hora de agora. Se for enviar mais tarde,
                    confira a saudação.
                  </span>
                </p>
              )}

              {m.canal === 'email' && para && (
                <div className="mo-assunto">
                  <span className="imv-rotulo">Para</span>
                  <p>{para}</p>
                </div>
              )}
              {m.assunto && (
                <div className="mo-assunto">
                  <span className="imv-rotulo">Assunto</span>
                  <p>{m.assunto}</p>
                </div>
              )}
              {/*
                ⭐ **A prévia mostra o texto COMO ELE SAI**, com o negrito e as
                cores. Antes era texto puro num `pre`; mostrar sem a formatação
                faria a pessoa copiar sem saber o que vai colar.

                ⛔ **Não é HTML cru na tela.** O servidor manda os trechos, e
                aqui eles viram elementos — o mesmo caminho das anotações do
                relatório, pela mesma razão: o que não vira trecho não aparece.
              */}
              <div className="mo-texto">
                <span className="imv-rotulo">Texto</span>
                <div className="mo-corrido">
                  {/*
                    ⛔ **A linha em branco do texto NÃO vira parágrafo aqui.** Ela
                    separa parágrafos no texto simples; na tela quem separa é a
                    margem, e manter as duas coisas dobrava o espaço.
                    ⚠️ Na CÓPIA ela continua indo, porque lá não há margem: é a
                    linha vazia que faz o Gmail separar.
                  */}
                  {m.paragrafos
                    .filter((par) => par.trechos.some((x) => x.t.trim()))
                    .map((par, i) => {
                      const dentro = par.trechos.map((tr, j) => {
                        const conteudo = tr.i ? <em>{tr.t}</em> : tr.t;
                        return tr.n ? (
                          <strong key={j} style={estiloDoTrecho(tr)}>{conteudo}</strong>
                        ) : (
                          <span key={j} style={estiloDoTrecho(tr)}>{conteudo}</span>
                        );
                      });
                      /* ⭐ O tópico aparece na prévia como aparece no e-mail. */
                      return par.lista ? (
                        <p key={i} className="mo-topico">{dentro}</p>
                      ) : (
                        <p key={i}>{dentro}</p>
                      );
                    })}
                </div>
              </div>
              {m.canal === 'email' && (
                <p className="mo-nota">
                  A assinatura institucional não entra aqui: o Gmail a insere sozinho ao escrever da caixa da casa.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </JanelaEmBlocos>
  );
}
